import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { OrderMatrixTable, type SupervisorFilter } from './OrderMatrixTable';
import { RunningOrderDetail } from './RunningOrderDetail';
import { completedRunningOrders, runningOrders } from './runningOrderOfficeData';
import { Tabs } from './Tabs';
import { useScreenInit } from '../useScreenInit.js';
type OrderView = 'running' | 'completed';
type RunningOrdersMatrixProps = {
  initialOrderView?: OrderView;
};
export function RunningOrdersMatrix({
  initialOrderView
}: RunningOrdersMatrixProps) {
  const screenInit = useScreenInit();
  const initialView: OrderView = initialOrderView ?? (screenInit.orderView === 'completed' ? 'completed' : 'running');
  const [activeView, setActiveView] = useState<OrderView>(initialView);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [supervisorFilter, setSupervisorFilter] = useState<SupervisorFilter>('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const visibleOrders = activeView === 'running' ? runningOrders : completedRunningOrders;
  const normalizedSearchQuery = searchQuery.trim().toLocaleLowerCase('de-DE');
  const filteredOrders = useMemo(() => {
    const startDateTimestamp = startDate ? parseDateInput(startDate) : null;
    const endDateTimestamp = endDate ? parseDateInput(endDate) : null;
    return visibleOrders.filter(order => {
      const searchableContent = `${order.id} ${order.product} ${order.operator}`.toLocaleLowerCase('de-DE');
      const matchesSearch = !normalizedSearchQuery || searchableContent.includes(normalizedSearchQuery);
      const hasSupervisorAdjustment = order.supervisorNote === 'Rezeptur angepasst';
      const matchesSupervisorFilter = supervisorFilter === 'all' || supervisorFilter === 'adjusted' && hasSupervisorAdjustment || supervisorFilter === 'standard' && !hasSupervisorAdjustment;
      const orderStartedAtTimestamp = parseGermanStartedAt(order.startedAt);
      const matchesStartDate = !startDateTimestamp || orderStartedAtTimestamp !== null && orderStartedAtTimestamp >= startDateTimestamp;
      const matchesEndDate = !endDateTimestamp || orderStartedAtTimestamp !== null && orderStartedAtTimestamp <= endDateTimestamp;
      return matchesSearch && matchesSupervisorFilter && matchesStartDate && matchesEndDate;
    });
  }, [endDate, normalizedSearchQuery, startDate, supervisorFilter, visibleOrders]);
  const selectedOrder = [...runningOrders, ...completedRunningOrders].find(order => order.id === selectedOrderId);
  useEffect(() => {
    const loadingTimeoutId = window.setTimeout(() => setIsLoading(false), 2000);
    return () => window.clearTimeout(loadingTimeoutId);
  }, []);
  useEffect(() => {
    setSelectedOrderId(null);
  }, [activeView]);
  if (selectedOrder) {
    return <RunningOrderDetail order={selectedOrder} onBack={() => setSelectedOrderId(null)} data-id="element-1110" />;
  }
  function resetFilters() {
    setSearchQuery('');
    setSupervisorFilter('all');
    setStartDate('');
    setEndDate('');
  }
  function handleOrderViewChange(value: string) {
    if (value === 'running' || value === 'completed') {
      setActiveView(value);
    }
  }
  return <section className="min-h-screen w-full bg-surface-subtle font-body text-text-primary" data-id="element-1111">
      <header className="border-b border-border bg-surface px-5 py-5 sm:px-8 sm:py-6 lg:px-10" data-id="element-1112">
        <motion.div initial={{
        opacity: 0,
        y: -8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.28,
        ease: 'easeOut'
      }} data-id="element-1113">
          <h1 className="font-heading text-3xl leading-tight sm:text-4xl" data-id="element-1114">
            Aufträge
          </h1>
        </motion.div>
      </header>

      <div className="mx-auto w-full max-w-[1540px] px-5 py-6 sm:px-8 sm:py-8 lg:px-10" data-id="element-1115">
        <motion.section initial="hidden" animate="visible" variants={{
        hidden: {
          opacity: 0,
          y: 12
        },
        visible: {
          opacity: 1,
          y: 0,
          transition: {
            duration: 0.32,
            ease: 'easeOut'
          }
        }
      }} className="overflow-hidden rounded-lg border border-border bg-surface shadow-sm" aria-label="Auftragsmatrix" data-id="element-1116">
          <Tabs ariaLabel="Auftragsansicht" className="[&_[role=tablist]]:pt-3" value={activeView} onValueChange={handleOrderViewChange} tabs={[{
          id: 'running',
          label: 'Aktuell',
          content: <OrderMatrixTable view="running" orders={filteredOrders} isLoading={isLoading} searchQuery={searchQuery} supervisorFilter={supervisorFilter} startDate={startDate} endDate={endDate} onSearchChange={setSearchQuery} onSupervisorFilterChange={setSupervisorFilter} onStartDateChange={setStartDate} onEndDateChange={setEndDate} onOpenOrder={setSelectedOrderId} onResetFilters={resetFilters} data-id="element-1118" />
        }, {
          id: 'completed',
          label: 'Abgeschlossen',
          content: <OrderMatrixTable view="completed" orders={filteredOrders} isLoading={isLoading} searchQuery={searchQuery} supervisorFilter={supervisorFilter} startDate={startDate} endDate={endDate} onSearchChange={setSearchQuery} onSupervisorFilterChange={setSupervisorFilter} onStartDateChange={setStartDate} onEndDateChange={setEndDate} onOpenOrder={setSelectedOrderId} onResetFilters={resetFilters} data-id="element-1119" />
        }]} data-id="element-1117" />
        </motion.section>
        <p className="mt-3 text-xs text-text-secondary sm:hidden" data-id="element-1120">
          Für weitere Spalten horizontal scrollen.
        </p>
      </div>
    </section>;
}
function parseGermanStartedAt(startedAt: string) {
  const match = startedAt.match(/^(\d{2})\.(\d{2})\.(\d{4})/);
  if (!match) {
    return null;
  }
  const [, day, month, year] = match;
  return new Date(Number(year), Number(month) - 1, Number(day)).getTime();
}
function parseDateInput(value: string) {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day).getTime();
}